import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Modal, Button } from '@ann/web-components';
import { ModalContent, ActionsContainer } from './ShareKnowledgeModal.styled';
import ShareKnowledgeModalContent from './ShareKnowledgeModalContent';
import PendingUsersList from './PendingUsersList';
import CurrentUsersList from './CurrentUsersList';
import { useMediaQueries } from '../../../../../utils/mediaQueries';
import { useStore } from '../../../../../hooks';
import { useToast } from '../../../../../components/toast';
import { addMembers, updateMember, removeMember } from '../../services/members';
import { useMembersContext } from '../../pages/KnowledgeHub/context';


// Mock data
const MOCK_PRIVILEGES = {
  knowledgeBasePrivileges: [
    { entityPrivilegeUuid: '1', privilegeName: 'Can view' },
    { entityPrivilegeUuid: '2', privilegeName: 'Can edit' },
    { entityPrivilegeUuid: '3', privilegeName: 'Can suggest' },
  ],
};

// Constants
const DEFAULT_PERMISSION = 'Can edit';

// Pure function - moved outside component to avoid recreation
const mapMemberRoleToPrivilege = (memberRole) => {
  const roleMap = {
    'ADMIN': 'Can edit',
    'APPROVER': 'Can view',
    'CONTRIBUTER': 'Can suggest',
  };
  return roleMap[memberRole] || 'Can view';
};

// Pure function - maps privilege name to member role
const mapPrivilegeToMemberRole = (privilegeName) => {
  const privilegeMap = {
    'Can edit': 'ADMIN',
    'Can view': 'APPROVER',
    'Can suggest': 'CONTRIBUTER',
  };
  return privilegeMap[privilegeName] || 'APPROVER';
};

// Pure function - maps member role to API role format (lowercase)
const mapMemberRoleToApiRole = (memberRole) => {
  const roleMap = {
    'ADMIN': 'admin',
    'APPROVER': 'approver',
    'CONTRIBUTER': 'contributer',
  };
  return roleMap[memberRole] || 'approver';
};


const ShareKnowledgeModal = observer(({
  isOpen,
  onClose,
  knowledgeBase,
  onShare,
}) => {
  const { knowledgeId } = useParams();
  const { usersStore } = useStore();
  const toast = useToast();
  const { members, loading: membersLoading, refreshMembers } = useMembersContext();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [isLoadingSharing, setIsLoadingSharing] = useState(false);

  // Extract knowledge base UUID - memoized to avoid recalculation
  const knowledgeBaseUuid = useMemo(() => {
    return knowledgeBase?.knowledgebase_uuid 
      || knowledgeBase?.knowledgebaseUuid 
      || knowledgeBase?.id 
      || knowledgeBase?.uuid
      || knowledgeId;
  }, [knowledgeBase?.knowledgebase_uuid, knowledgeBase?.knowledgebaseUuid, knowledgeBase?.id, knowledgeBase?.uuid, knowledgeId]);

  // Load users from UsersStore only if not already loaded
  useEffect(() => {
    if (!isOpen) return;
    
    // Only load if store is empty (users not loaded yet)
    // If users are already loaded from Main page, skip loading
    if (usersStore.users.length === 0) {
      usersStore.getUserList({
        page: 1,
        page_size: 100, // Get all users
        order_by: "fullName",
        order_direction: "ASC",
      }).catch((error) => {
        console.error('Error loading users from UsersStore:', error);
      });
    }
    // Only depend on isOpen and users length, usersStore is stable (MobX singleton)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, usersStore.users.length]);

  // Load current members from context when modal opens
  useEffect(() => {
    if (!isOpen || !knowledgeBaseUuid) return;
    
    refreshMembers(knowledgeBaseUuid);
  }, [isOpen, knowledgeBaseUuid, refreshMembers]);

  // Transform members from context to format expected by CurrentUsersList
  const currentUsers = useMemo(() => {
    if (!members || !Array.isArray(members)) return [];
    
    return members.map((member) => {
      const memberUuid = member.memberUuid || member.member_uuid;
      const userUuid = member.userUuid || member.user_uuid || member.user_id || member.userId;
      const userFullName = member.userFullName || member.user_full_name || 'Unknown';
      const userEmail = member.userEmail || member.user_email || '';
      const memberRole = member.memberRole || member.member_role;

      return {
        grantee_uuid: userUuid,
        member_uuid: memberUuid,
        grantee_name: userFullName,
        grantee_email: userEmail,
        grantee_type: 'user',
        privileges: [{ privilege_name: mapMemberRoleToPrivilege(memberRole) }],
      };
    });
  }, [members]);

  // Format users from store for Autocomplete
  const usersList = useMemo(() => {
    if (!usersStore.users || !Array.isArray(usersStore.users)) {
      return [];
    }
    return usersStore.users.map((user) => ({
      user_uuid: user.user_uuid || user.userUuid || user.uuid,
      full_name: user.fullName || user.full_name || "Unknown",
      email: user.email || "",
    }));
  }, [usersStore.users]);

  const handleUsersChange = useCallback((newSelectedUsers) => {
    setSelectedUsers((prevSelectedUsers) => {
      // Find newly added users (users that are in newSelectedUsers but not in previous selectedUsers)
      const previousUserIds = new Set(prevSelectedUsers.map(u => u.user_uuid));
      const newlyAddedUsers = newSelectedUsers.filter(
        user => !previousUserIds.has(user.user_uuid)
      );

      // Automatically add newly selected users to pending list with default permission
      if (newlyAddedUsers.length > 0) {
        const newPendingUsers = newlyAddedUsers.map((user) => ({
          grantee_uuid: user.user_uuid,
          grantee_name: user.full_name,
          grantee_email: user.email || '',
          privileges: [{ privilege_name: DEFAULT_PERMISSION }],
        }));

        setPendingUsers((prevPending) => [...prevPending, ...newPendingUsers]);
      }

      // Always clear selectedUsers to keep the Autocomplete empty
      // Selected users only appear in pendingUsers list
      return [];
    });
  }, []);

  const handleUpdatePendingPermission = useCallback((userId, newPermission) => {
    setPendingUsers((prevPending) =>
      prevPending.map((user) =>
        user.grantee_uuid === userId
          ? { ...user, privileges: [{ privilege_name: newPermission }] }
          : user
      )
    );
  }, []);

  const handleRemovePending = useCallback((userId) => {
    setPendingUsers((prevPending) => prevPending.filter((user) => user.grantee_uuid !== userId));
  }, []);

  const reloadCurrentMembers = useCallback(async () => {
    if (!knowledgeBaseUuid) return;
    await refreshMembers(knowledgeBaseUuid);
  }, [knowledgeBaseUuid, refreshMembers]);

  const handleUpdateCurrentPermission = useCallback(async (userId, userType, newPermission) => {
    if (!knowledgeBaseUuid) {
      toast.error({
        title: 'Error',
        message: 'Knowledge base UUID is required',
      });
      return;
    }

    // Find the user in currentUsers to get member_uuid
    const userToUpdate = currentUsers.find(user => user.grantee_uuid === userId);
    if (!userToUpdate || !userToUpdate.member_uuid) {
      toast.error({
        title: 'Error',
        message: 'Member information not found',
      });
      return;
    }

    // Map privilege name to member role (uppercase)
    const memberRole = mapPrivilegeToMemberRole(newPermission);
    // Map to lowercase for API
    const apiRole = mapMemberRoleToApiRole(memberRole);

    try {
      await updateMember({
        knowledge_base_uuid: knowledgeBaseUuid,
        member_uuid: userToUpdate.member_uuid,
        user_uuid: userId,
        new_role: apiRole,
      });

      // After successful update, reload current members
      await reloadCurrentMembers();

      toast.success({
        title: 'Permission updated',
        message: 'Member role updated successfully',
      });
    } catch (error) {
      console.error('Error updating member role:', error);
      
      // Error message is already normalized by handleApiError in the service
      const errorMessage = error.message || 'An error occurred while updating member role.';
      
      toast.error({
        title: 'Error',
        message: errorMessage,
      });
    }
  }, [knowledgeBaseUuid, currentUsers, reloadCurrentMembers, toast]);

  const handleRemoveCurrent = useCallback(async (userId, userType) => {
    if (!knowledgeBaseUuid) {
      toast.error({
        title: 'Error',
        message: 'Knowledge base UUID is required',
      });
      return;
    }

    // Find the user in currentUsers to get member_uuid
    const userToRemove = currentUsers.find(user => user.grantee_uuid === userId);
    if (!userToRemove || !userToRemove.member_uuid) {
      toast.error({
        title: 'Error',
        message: 'Member information not found',
      });
      return;
    }

    try {
      await removeMember({
        knowledge_base_uuid: knowledgeBaseUuid,
        member_uuid: userToRemove.member_uuid,
        user_uuid: userId,
      });

      // After successful removal, reload current members
      await reloadCurrentMembers();

      toast.success({
        title: 'Member removed',
        message: 'Member removed successfully',
      });
    } catch (error) {
      console.error('Error removing member:', error);
      
      // Error message is already normalized by handleApiError in the service
      const errorMessage = error.message || 'An error occurred while removing member.';
      
      toast.error({
        title: 'Error',
        message: errorMessage,
      });
    }
  }, [knowledgeBaseUuid, currentUsers, reloadCurrentMembers, toast]);

  const handleShareClick = useCallback(async () => {
    if (pendingUsers.length === 0) return;

    setIsLoadingSharing(true);
    
    // Capture current pending users before clearing
    const currentPendingUsers = [...pendingUsers];
    const membersToAdd = currentPendingUsers.map((user) => {
      const privilegeName = user.privileges[0]?.privilege_name || DEFAULT_PERMISSION;
      return {
        user_uuid: user.grantee_uuid,
        member_role: mapPrivilegeToMemberRole(privilegeName),
      };
    });

    try {
      if (!knowledgeBaseUuid) {
        throw new Error('Knowledge base UUID is required');
      }

      // Call API to add members
      // getApiResponse will throw error if status_code is 40X (even with HTTP 200)
      await addMembers({
        knowledge_base_uuid: knowledgeBaseUuid,
        members: membersToAdd,
      });

      // After successful API call, reload current members
      await reloadCurrentMembers();
      
      // Show success toast
      const userCount = currentPendingUsers.length;
      const userText = userCount === 1 ? 'user' : 'users';
      const verbText = userCount === 1 ? 'has' : 'have';
      toast.success({
        title: 'User added successfully',
        message: `${userCount} ${userText} ${verbText} been added successfully`,
      });
      
      // Clear pending users and selected users after success
      setPendingUsers([]);
      setSelectedUsers([]);
      
      if (onShare) {
        onShare(currentPendingUsers);
      }
      
      // Close modal after successful operation
      onClose();
    } catch (error) {
      console.error('Error adding members to knowledge base:', error);
      
      // Error message is already normalized by handleApiError in the service
      // Just use error.message which contains the extracted message from msgCode
      const errorMessage = error.message || 'An error occurred while adding members to knowledge base.';
      
      toast.error({
        title: 'Error',
        message: errorMessage,
      });
    } finally {
      setIsLoadingSharing(false);
    }
  }, [pendingUsers, knowledgeBaseUuid, reloadCurrentMembers, toast, onShare, onClose]);

  const { isMobile, isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();
  
  const knowledgeBaseName = useMemo(() => {
    return knowledgeBase?.name || 'Sales Config';
  }, [knowledgeBase?.name]);

  const modalWidth = useMemo(() => {
    if (isMobile || isIpadAirPortrait || isIpadProPortrait) {
      return '95%';
    }
    return '900px';
  }, [isMobile, isIpadAirPortrait, isIpadProPortrait]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium" width={modalWidth}>
      <Modal.Header>
        <Modal.Header.Title>
          Add Users
        </Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          <ShareKnowledgeModalContent
            knowledgeBaseName={knowledgeBaseName}
            selectedUsers={selectedUsers}
            onUsersChange={handleUsersChange}
            usersList={usersList}
          />

          <PendingUsersList
            pendingUsers={pendingUsers}
            privileges={MOCK_PRIVILEGES}
            onUpdatePermission={handleUpdatePendingPermission}
            onRemove={handleRemovePending}
          />

          <CurrentUsersList
            shouldShow={currentUsers.length > 0 || membersLoading}
            currentUsers={currentUsers}
            isLoading={membersLoading}
            privileges={MOCK_PRIVILEGES}
            onUpdatePermission={handleUpdateCurrentPermission}
            onRemove={handleRemoveCurrent}
          />
        </ModalContent>
      </Modal.Body>
      <Modal.Actions align="end">
        {pendingUsers.length > 0 && (
          <Button
            label="Add"
            size="medium"
            type="primary"
            onClick={handleShareClick}
            disabled={isLoadingSharing}
          />
        )}
      </Modal.Actions>
    </Modal>
  );
});

export default ShareKnowledgeModal;

